import { useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'
import { DialogTrigger } from '@radix-ui/react-dialog'

import type { PhotosApi } from '@jotta/types/PhotosApi'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgPlus from '@jotta/ui/SvgPlus'

import type { PhotoActionDispatcher } from '../../PhotoActions/useDispatchPhotoActionMutation'
import { useDispatchPhotoActionMutation } from '../../PhotoActions/useDispatchPhotoActionMutation'
import { BaseAlbumThumb } from '../AlbumThumb/BaseAlbumThumb'
import type { CreateAlbumDialogProps } from '../Dialogs/CreateAlbumDialog'
import { CreateAlbumDialog } from '../Dialogs/CreateAlbumDialog'

const debug = Debug('jotta:photos:CreateAlbumFromAlbumThumb')

interface CreateAlbumFromAlbumThumbProps extends Partial<CreateAlbumDialogProps> {
  dispatch?: PhotoActionDispatcher
  onAlbumCreated?: (album: PhotosApi.AlbumInfo) => void
}
export const CreateAlbumFromAlbumThumb =
  observer<CreateAlbumFromAlbumThumbProps>(function CreateAlbumFromAlbumThumb({
    dispatch,
    onAlbumCreated,
    ...props
  }) {
    const [open, setOpen] = useState(false)
    const onClose = props.onClose || setOpen
    const isOpen = props.open ?? open
    const mutation = useDispatchPhotoActionMutation(
      'CREATE_ALBUM',
      {
        onSettled: async albumPromise => {
          const album = await albumPromise
          if (album && onAlbumCreated) {
            onAlbumCreated(album)
          }
          debug('onSettled', album)
          onClose(false)
        },
      },
      dispatch,
    )
    return (
      <CreateAlbumDialog
        open={isOpen}
        onClose={onClose}
        createAlbumFn={async (ids, title) => {
          const album = await mutation.mutateAsync([ids, title])
          return album
        }}
        triggerElement={
          <DialogTrigger data-testid="ACTION_CREATE_ALBUM">
            <BaseAlbumThumb key={'new_album'}>
              <PlainBrandIcon
                icon={SvgPlus}
                width={24}
                height={24}
                className="text-sx-primary"
              />
              <p className="mb-0 mt-4 text-base text-primary">
                <Trans>New album</Trans>
              </p>
            </BaseAlbumThumb>
          </DialogTrigger>
        }
        {...props}
      />
    )
  })
